/**
 * Simple vanilla TypeScript router for SPA navigation
 */

export interface Route {
  path: string
  handler: () => Promise<void> | void
  title?: string
  requiresAuth?: boolean
}

export class Router {
  private routes: Map<string, Route> = new Map()
  private currentPath: string = ''

  constructor() {
    // Listen for browser navigation events
    window.addEventListener('popstate', () => this.handleNavigation())
    
    // Intercept link clicks for SPA navigation
    document.addEventListener('click', (e) => this.handleLinkClick(e))
  }

  /**
   * Register a route with its handler
   */
  addRoute(path: string, handler: () => Promise<void> | void, title?: string): void {
    this.routes.set(path, { path, handler, title })
  }

  /**
   * Register a route with its handler (alias for compatibility)
   */
  register(path: string, handler: () => Promise<void> | void, requiresAuth: boolean = false, title?: string): void {
    console.log(`Registering route ${path}, auth required: ${requiresAuth}`)
    this.routes.set(path, { path, handler, title, requiresAuth })
  }

  /**
   * Navigate to a specific path
   */
  async navigate(path: string): Promise<void> {
    if (path === this.currentPath) return

    // Update browser history
    window.history.pushState(null, '', path)
    await this.handleNavigation()
  }

  /**
   * Handle navigation to current path
   */
  private async handleNavigation(): Promise<void> {
    const path = window.location.pathname
    this.currentPath = path

    // Find matching route
    const route = this.routes.get(path) || this.routes.get('*') // fallback to wildcard

    if (route) {
      // Check authentication if required
      if (route.requiresAuth && !this.isAuthenticated()) {
        console.log(`Route ${path} requires authentication, redirecting to login`)
        this.navigate('/login')
        return
      }

      // Update page title if provided
      if (route.title) {
        document.title = route.title
      }
      
      // Execute route handler
      await route.handler()
      
      // Scroll to top after navigation
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      console.warn(`No route handler found for path: ${path}`)
    }
  }

  /**
   * Check if user is authenticated
   */
  private isAuthenticated(): boolean {
    // Check for auth token in localStorage
    const token = localStorage.getItem('auth_token')
    const userData = localStorage.getItem('user_data')
    return !!(token && userData)
  }

  /**
   * Intercept link clicks for SPA navigation
   */
  private handleLinkClick(e: MouseEvent): void {
    const target = e.target as HTMLElement
    const link = target.closest('a')

    if (!link) return

    const href = link.getAttribute('href')
    
    // Only handle internal links
    if (href && href.startsWith('/') && !href.startsWith('//')) {
      e.preventDefault()
      this.navigate(href)
    }
  }

  /**
   * Get current path
   */
  getCurrentPath(): string {
    return this.currentPath
  }

  /**
   * Start the router and handle initial navigation
   */
  async start(): Promise<void> {
    await this.handleNavigation()
  }
}

// Export singleton router instance
export const router = new Router()
